import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../../components/PublicChrome/public.css";
import { EmptyPlanSearch } from "./components/EmptyPlanSearch";
import { PlanDetailsModal } from "./components/PlanDetailsModal";
import { ProductCard } from "./components/ProductCard";
import { normalize } from "./lib/format";
import {
  PLAN_AREAS,
  groupVisiblePlans,
  isChoaOrTapPlan,
  visiblePlans,
  type PlanArea,
} from "./lib/planGroups";
import { Plan } from "./types";

const OFFER_PRICE_NOTE =
  "[OQ-10 / BUG-03 — oferta e preço final: decisão do CEO · não publicar este aviso]";

const CHOA_TAP_NOTE =
  "[OQ-1 / OQ-2 — planos CHOA/TAP (nome, conteúdo, preço) em aberto: decisão do CEO · não publicar este aviso]";

const placeholderStyle = {
  background: "var(--ph-bg)",
  border: "1px solid var(--ph-border)",
  color: "var(--ph-fg)",
};

export interface PlansPageProps {
  plans: Plan[];
  loading: boolean;
  error: string | null;
  subscribingId: string | null;
  onSubscribe: (plan: Plan) => void;
}

function SkeletonCard() {
  return (
    <div
      className="h-72 animate-pulse rounded-card border"
      style={{
        background: "var(--v3l-surface-alt)",
        borderColor: "var(--v3l-border-divider)",
      }}
      aria-hidden
    />
  );
}

function planCountLabel(count: number): string {
  return count === 1 ? "1 plano" : `${count} planos`;
}

export function PlansPage({ plans, loading, error, subscribingId, onSubscribe }: PlansPageProps) {
  const [area, setArea] = useState<PlanArea>("todos");
  const [query, setQuery] = useState("");
  const [details, setDetails] = useState<Plan | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const shown = visiblePlans(plans, area, query);
  const groups = groupVisiblePlans(shown);

  const requestedSlug = searchParams.get("plano");
  useEffect(() => {
    if (!requestedSlug || loading) return;
    const plan = plans.find((p) => p.slug === requestedSlug);
    if (plan) setDetails(plan);
    setSearchParams(
      (params) => {
        params.delete("plano");
        return params;
      },
      { replace: true },
    );
  }, [requestedSlug, loading, plans, setSearchParams]);

  return (
    <div className="public-pages min-w-0 max-w-full">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-6 overflow-x-hidden px-4 py-6 md:px-6 lg:px-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex w-full min-w-0 flex-col gap-2 md:flex-1">
            <label htmlFor="plans-search" className="text-sm font-semibold">
              Buscar plano ou concurso
            </label>
            <input
              id="plans-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ex.: CBMGO, Soldado, Medicina"
              className="min-h-[44px] w-full min-w-0 rounded-chip border px-3 text-base"
              style={{
                background: "var(--v3l-surface-base)",
                color: "var(--v3l-text-primary)",
                borderColor: "var(--v3l-border-divider)",
              }}
            />
          </div>

          <div role="group" aria-label="Área" className="flex max-w-full flex-wrap gap-2">
            {PLAN_AREAS.map((item) => {
              const pressed = area === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={pressed}
                  onClick={() => setArea(item.id)}
                  className="min-h-[44px] whitespace-nowrap rounded-chip px-4 text-sm font-medium"
                  style={
                    pressed
                      ? {
                          background: "var(--v3l-surface-navy-band)",
                          color: "var(--v3l-band-navy-heading)",
                          border: "1px solid transparent",
                        }
                      : {
                          background: "var(--v3l-surface-base)",
                          color: "var(--v3l-text-primary)",
                          border: "1px solid var(--v3l-border-divider)",
                        }
                  }
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <p className="rounded-chip px-3 py-2 text-sm leading-5" style={placeholderStyle}>
          {OFFER_PRICE_NOTE}
        </p>

        {shown.some(isChoaOrTapPlan) && (
          <p className="rounded-chip px-3 py-2 text-sm leading-5" style={placeholderStyle}>
            {CHOA_TAP_NOTE}
          </p>
        )}

        {error && (
          <p
            role="alert"
            className="rounded-chip px-4 py-3 text-sm"
            style={{
              background: "var(--danger-soft)",
              border: "1px solid var(--danger-border)",
              color: "var(--danger)",
            }}
          >
            {error}
          </p>
        )}

        {loading && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" aria-hidden>
            {Array.from({ length: 3 }, (_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        )}

        {!loading && (
          <p className="text-sm font-medium" aria-live="polite">
            {planCountLabel(shown.length)}
          </p>
        )}

        {!loading && shown.length === 0 && normalize(query) !== "" && (
          <EmptyPlanSearch query={query.trim()} onClear={() => setQuery("")} />
        )}

        {!loading && shown.length === 0 && normalize(query) === "" && (
          <p className="text-sm">Nenhum plano para mostrar.</p>
        )}

        {groups.map((group) => (
          <section key={group.id} className="flex flex-col gap-3">
            <h2 className="text-lg font-semibold">{group.label}</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {group.plans.map((plan) => (
                <ProductCard
                  key={plan.id}
                  plan={plan}
                  onSubscribe={onSubscribe}
                  onDetails={setDetails}
                  loading={subscribingId === plan.id}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <PlanDetailsModal
        plan={details}
        loading={details ? subscribingId === details.id : false}
        onClose={() => setDetails(null)}
        onSubscribe={(plan) => {
          setDetails(null);
          onSubscribe(plan);
        }}
      />
    </div>
  );
}
